import {
  OrdersFormFieldInputModel,
  OrdersFormFieldReplacementDataInputModel,
  OrdersFormFieldReplacementInputModel,
  OrdersFormInputModel,
  OrdersFormViewModel,
  OrderType,
  Visibility,
} from '@ail/orders-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useHistory, useParams } from 'react-router-dom'
import {
  useGetAllCustomersQuery,
  useGetAllUnitsQuery,
  useGetTenantsQuery,
  useGetTenantUsersQuery,
} from '../../../redux/api/customerApi'
import {
  useGetApprovalProcessConfigsQuery,
  useGetOrdersFormsQuery,
  usePostOrdersFormMutation,
  usePutOrdersFormMutation,
} from '../../../redux/api/ordersApi'
import Alert from '../../common/Alert/Alert'
import { FormBox } from '../../common/FormBox/FormBox'
import RequestEdit from '../../Orders/RequestEdit/RequestEdit'
import './NewForm.scss'
import { Loader } from '../../common/Loader/Loader'

// #region Types & Constants

interface OwnerInfo {
  type: string
  name: string
  tenantId: string
}

const BLANK_FORM: OrdersFormInputModel = {
  allowedForInternalUsers: false,
  ownerTenant: '',
  owner: '',
  parent: '',
  filterCompamniesByMatchingApprovalFlows: false,
  orderType: OrderType.Generic,
  name: '',
  tr: {},
  genericTooltip: {},
  formFields: [],
  sections: [],
  approvalWorkflows: [],
  approvalWorkflowsAdv: {},
}

const BLANK_FIELD: OrdersFormFieldInputModel = {
  name: '',
  type: '',
  section: '',
  fieldsToListen: [],
  tr: {},
  unitsTrSingular: {},
  unitsTrPlural: {},
  tooltip: {},
  required: false,
  disabled: false,
  fuelCardsLimit: 0,
  minimumValue: 0,
  maximumValue: 0,
  step: 0,
  options: [],
  visibleWhen: {},
  enabledWhen: {},
  clearOnChange: [],
  avoidToDuplicate: false,
  isHiddenOnForm: false,
  isHiddenOnPrint: false,
  isHiddenOnDcs: false,
  platesRequired: false,
}


const BLANK_FIELD_REPLACEMENT: OrdersFormFieldReplacementDataInputModel = {
  options: [],
}

const BLANK_INTERNAL_SOURCE: OrdersFormFieldReplacementInputModel = {
  triggerFields: {},
  newData: BLANK_FIELD_REPLACEMENT,
}

// #endregion

export function NewForm() {
  // #region State
  const history = useHistory()
  const { id: formId } = useParams<{ id: string }>()
  const { t } = useTranslation('common')
  const isNewForm = formId === 'new'

  const [expandedWorkflowInfo, setExpandedWorkflowInfo] = useState<Partial<Record<string, boolean>>>({})
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [previewForm, setPreviewForm] = useState<OrdersFormInputModel>()
  const [savingError, setSavingError] = useState<string>()
  const [currentForm, setCurrentForm] = useState<OrdersFormInputModel>()
  const [currentFormJson, setCurrentFormJson] = useState<string>('')
  const [jsonChanged, setJsonChanged] = useState<boolean>(false)
  // #endregion

  // #region API
  const tenants = useGetTenantsQuery()
  const customers = useGetAllCustomersQuery()
  const units = useGetAllUnitsQuery()
  const orderForms = useGetOrdersFormsQuery(isNewForm ? skipToken : undefined)
  const users = useGetTenantUsersQuery(currentForm?.ownerTenant ?? skipToken)
  const workflows = useGetApprovalProcessConfigsQuery()

  const [postOrderForm] = usePostOrdersFormMutation()
  const [putOrderForm] = usePutOrdersFormMutation()
  // #endregion

  // #region Form Handling
  useEffect(() => {
    if (isNewForm) {
      setCurrentForm(BLANK_FORM)
    } else {
      const form = orderForms.currentData?.find((f) => f.id.toLowerCase() === formId.toLowerCase())
      if (form) {
        const filteredForm: OrdersFormInputModel = { ...form }
        if ('approvalProcessConfigs' in filteredForm) delete filteredForm['approvalProcessConfigs']
        setCurrentForm(filteredForm)
      }
    }
  }, [formId, isNewForm, orderForms.currentData])

  useEffect(() => {
    setCurrentFormJson(JSON.stringify(currentForm, null, '\t'))
  }, [currentForm])

  useEffect(() => {
    const form = currentForm ? { ...currentForm } : { ...BLANK_FORM }
    setPreviewForm(form)
  }, [currentForm])

  useEffect(() => {
    setPreviewForm((curr) => (curr ? { ...curr } : curr))
  }, [showPreview])

  const workflowIds = useMemo(() => {
    const ids: string[] = []
    for (const id of currentForm?.approvalWorkflows ?? []) {
      ids.push(id)
    }
    for (const id of Object.keys(currentForm?.approvalWorkflowsAdv ?? {})) {
      ids.push(id)
    }
    return ids.filter((item, index) => ids.indexOf(item) === index)
  }, [currentForm?.approvalWorkflows, currentForm?.approvalWorkflowsAdv])

  const workflowTriggers = useMemo(() => {
    const result: Partial<Record<string, string[]>> = {}
    for (const id of workflowIds) {
      const triggers = result[id] ?? []
      result[id] = triggers
      const workflowAdv = currentForm?.approvalWorkflowsAdv?.[id]
      if (!workflowAdv) {
        continue
      }
      for (const [key, values] of Object.entries(workflowAdv)) {
        if (key === 'costCenterId') {
          const t = values.map(
            (v) => units.currentData?.find((u) => u.unit?.id === v)?.unit?.name ?? 'Cost center (Unknown)'
          )
          triggers.push(...t)
        } else if (key === 'companyId') {
          const t = values.map(
            (v) => customers.currentData?.find((c) => c.customer?.id === v)?.customer?.name ?? 'Company (Unknown)'
          )
          triggers.push(...t)
        } else {
          const fieldExists = currentForm.formFields?.some((f) => f.name === key)
          const t = values.map((v) => (fieldExists ? `${key}:${v}` : `${key}:${v} (Unknown)`))
          triggers.push(...t)
        }
      }
    }
    return result
  }, [currentForm, customers.currentData, units.currentData, workflowIds])

  const workflowNames = useMemo(() => {
    return workflowIds.reduce((result, id) => {
      result[id] = workflows.currentData?.find((w) => w.id === id)?.name
      return result
    }, {} as Partial<Record<string, string>>)
  }, [workflowIds, workflows.currentData])

  const workflowStates = useMemo(() => {
    return workflowIds.reduce((result, id) => {
      const hasWorkflow = currentForm?.approvalWorkflows?.some((wid) => wid === id)
      const hasWorkflowAdv = !!currentForm?.approvalWorkflowsAdv?.[id]
      const triggersValid = workflowTriggers[id]?.every((t) => !t.includes('(Unknown)'))
      result[id] = hasWorkflow && hasWorkflowAdv && triggersValid ? 'valid' : hasWorkflow ? 'notice' : 'error'
      return result
    }, {} as Partial<Record<string, 'error' | 'notice' | 'valid'>>)
  }, [currentForm?.approvalWorkflows, currentForm?.approvalWorkflowsAdv, workflowIds, workflowTriggers])

  const sortedWorkflowIds = useMemo(() => {
    return workflowIds.toSorted((a, b) => {
      const aState = workflowStates[a]!
      const bState = workflowStates[b]!
      return aState.localeCompare(bState)
    })
  }, [workflowIds, workflowStates])

  const getWorkflowStateIcon = useCallback(
    (id: string) => {
      const state = workflowStates[id]
      return state === 'error' ? 'icon-error' : state === 'notice' ? 'icon-warning' : 'icon-check'
    },
    [workflowStates]
  )

  const getWorkflowStateColor = useCallback(
    (id: string) => {
      const state = workflowStates[id]
      return state === 'error' ? '#fe1f17' : state === 'notice' ? '#f80' : 'green'
    },
    [workflowStates]
  )

  const validateForm = useCallback(() => {
    const errors: string[] = []

    const duplicatedFields = currentForm?.formFields?.filter((el, i) =>
      currentForm.formFields?.some((q, ix) => q.name === el.name && i !== ix)
    )

    if (duplicatedFields?.length) {
      errors.push(`Duplicated names are found: ${duplicatedFields.map((q) => q.name).join(', ')}`)
    }

    const invalidWorkflowIds = workflowIds.filter((id) => workflowStates[id] === 'error')
    if (invalidWorkflowIds.length) {
      errors.push(`Missing approval workflow specified in Adv: ${invalidWorkflowIds.join(', ')}`)
    }

    return errors
  }, [currentForm?.formFields, workflowIds, workflowStates])

  const navigateToGrid = useCallback(
    (e?: React.MouseEvent<HTMLAnchorElement>) => {
      if (e) e.preventDefault()
      history.push('/RequestsManagement')
    },
    [history]
  )
  // #endregion

  // #region Variables
  const ownerInfo = useMemo((): OwnerInfo => {
    const ownerSearchId = currentForm?.owner ?? ''
    if (ownerSearchId.length <= 35) {
      return { type: '', name: '', tenantId: '' }
    }

    const tenant = tenants.data?.find((t) => t.id === ownerSearchId)
    if (tenant) {
      return { type: 'TENANT', name: tenant.name, tenantId: tenant.id }
    }

    const user = users.data?.find((u) => u.id === ownerSearchId)
    if (user) {
      return { type: 'USER', name: user.name || '', tenantId: currentForm?.ownerTenant || '' }
    }

    const customer = customers.data?.find((c) => c.customer?.id === ownerSearchId)?.customer
    if (customer) {
      return { type: 'CUSTOMER', name: customer.name, tenantId: customer.tenantId ?? '' }
    }

    const unit = units.data?.find((u) => u.unit?.id === ownerSearchId)?.unit
    if (unit) {
      return { type: 'UNIT', name: unit.name ?? '', tenantId: unit.tenantId ?? '' }
    }

    return { type: '', name: '', tenantId: '' }
  }, [currentForm?.owner, currentForm?.ownerTenant, tenants.data, users.data, customers.data, units.data])

  const ownerValidation = useMemo(() => {
    const hasOwnerInfo = !!(ownerInfo.name && ownerInfo.type)
    const hasTenantMismatch = ownerInfo.tenantId && ownerInfo.tenantId !== currentForm?.ownerTenant
    const missingParentTenant = !ownerInfo.tenantId && hasOwnerInfo

    return { hasOwnerInfo, hasTenantMismatch, missingParentTenant }
  }, [ownerInfo, currentForm?.ownerTenant])

  const isLoading = tenants.isFetching || customers.isFetching || orderForms.isFetching || users.isFetching
  // #endregion

  // #region Event Handlers
  const handleSubmit = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()

      if (!currentForm) {
        setSavingError('Form data is invalid or missing.')
        return
      }

      const errors = validateForm()
      if (errors.length) {
        alert(errors.join(';\n '))
        return
      }

      try {
        if (isNewForm) {
          await postOrderForm(currentForm).unwrap()
        } else {
          await putOrderForm({ id: formId, model: currentForm }).unwrap()
        }
        alert(`Form saved! Form ID ${formId}`)
        navigateToGrid()
      } catch {
        setSavingError('Failed to save form.')
      }
    },
    [currentForm, validateForm, isNewForm, formId, navigateToGrid, postOrderForm, putOrderForm]
  )

  const handleJsonChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentFormJson(e.target.value)
    setJsonChanged(true)
  }, [])

  const handleApplyJson = useCallback(() => {
    try {
      const parsed = JSON.parse(currentFormJson) as Partial<OrdersFormInputModel>
      const formFields = (parsed.formFields ?? []).map((field): OrdersFormFieldInputModel => {
        const merged: OrdersFormFieldInputModel = {
          ...BLANK_FIELD,
          ...field,
          internalSource: field.internalSource
            ? {
                ...BLANK_INTERNAL_SOURCE,
                ...field.internalSource,
                newData: {
                  ...BLANK_FIELD_REPLACEMENT,
                  ...field.internalSource.newData
                },
              }
            : undefined,
        }
        if (merged.isHiddenOnForm) merged.required = false
        return merged
      })
      setCurrentForm({ ...BLANK_FORM, ...parsed, formFields })
      setJsonChanged(false)
      return true
    } catch {
      alert('Error parsing form JSON')
      return false
    }
  }, [currentFormJson])

  const handleTenantChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const ownerTenant = e.target.value
      if (handleApplyJson()) {
        setCurrentForm((current) => {
          return current ? { ...current, ownerTenant } : current
        })
      }
    },
    [handleApplyJson]
  )

  const handleVisibilityChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const visibility = e.target.value as Visibility
      if (handleApplyJson()) {
        setCurrentForm((current) => {
          return current ? { ...current, visibility } : current
        })
      }
    },
    [handleApplyJson]
  )

  const handleValidFromChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const validFrom = new Date(e.target.value).toJSON()
      if (handleApplyJson()) {
        setCurrentForm((current) => {
          return current ? { ...current, validFrom } : current
        })
      }
    },
    [handleApplyJson]
  )

  const handleValidToChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const validTo = new Date(e.target.value).toJSON()
      if (handleApplyJson()) {
        setCurrentForm((current) => {
          return current ? { ...current, validTo } : current
        })
      }
    },
    [handleApplyJson]
  )

  const toggleWorkflowInfo = useCallback((id: string) => {
    setExpandedWorkflowInfo((current) => ({ ...current, [id]: !current[id] }))
  }, [])
  // #endregion

  // #region Render

  return (
    <Loader className="orders-form-edit" blocking={isLoading}>
      <div className="orders-form-edit-left">
        <FormBox
          icon="icon-edit"
          title="Edit form"
          actions={<button onClick={() => setShowPreview(!showPreview)}>Show/Hide preview</button>}
        >
          <div className="form-control">
            <label>Select owner tenant ID</label>
            <div className="selectArea">
              <select onChange={handleTenantChange} value={currentForm?.ownerTenant ?? ''}>
                <option disabled value={''}>
                  Select owner Tenant
                </option>
                {tenants.currentData?.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
              <i className="icon-bottom"></i>
            </div>
          </div>

          <div className="form-control">
            <label>Owner ID</label>
            <input
              type="text"
              name={`fieldnameveryunique${Date.now()}`}
              autoComplete="off"
              list="autocompleteOff"
              value={currentForm?.owner || ''}
              placeholder="Owner Id"
            />
            {ownerValidation.hasOwnerInfo && (
              <span className="owner-details">
                {ownerInfo.type} - {ownerInfo.name}
              </span>
            )}
            {ownerValidation.hasTenantMismatch && <span className="warning">TENANTS ARE DIFFERENT</span>}
            {ownerValidation.missingParentTenant && <span className="error">Parent tenant is missing</span>}
          </div>

          <div className="form-control">
            <label>Enter new form's JSON</label>
            <textarea className="form-json" onChange={handleJsonChange} value={currentFormJson} />
            <div className="form-json-apply">
              {jsonChanged && <button onClick={handleApplyJson}>Apply JSON</button>}
            </div>
          </div>
        </FormBox>

        {currentForm && (
          <FormBox title="Request visibility" icon="icon-requests-man">
            <div className="orders-form-edit-visibility">
              <div className="form-control">
                <label htmlFor="visibility">Visibility status</label>
                <div className="selectArea">
                  <select
                    id="visibility"
                    name="Select visibility"
                    value={currentForm?.visibility ?? ''}
                    onChange={handleVisibilityChange}
                  >
                    <option value="" disabled>
                      Select Visibility
                    </option>
                    {Object.values(Visibility).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <i className="icon-bottom" />
                </div>
              </div>

              <div className="form-control">
                <label htmlFor="datefrom">Valid From</label>
                <div className="dateArea">
                  <input
                    id="datefrom"
                    name="Valid From"
                    type="date"
                    value={currentForm?.validFrom?.slice(0, 10)}
                    onChange={handleValidFromChange}
                    placeholder="Valid From"
                  />
                  <i className="icon-calendar" />
                </div>
              </div>

              <div className="form-control">
                <label htmlFor="dateto">Valid To</label>
                <div className="dateArea">
                  <input
                    id="dateto"
                    name="Valid To"
                    type="date"
                    value={currentForm?.validTo?.slice(0, 10)}
                    onChange={handleValidToChange}
                    placeholder="Valid To"
                  />
                  <i className="icon-calendar" />
                </div>
              </div>
            </div>
          </FormBox>
        )}

        <FormBox>
          <div className="orders-form-edit-actions">
            <a onClick={navigateToGrid} className="bordered-button red">
              <i className="icon-close"></i> CANCEL
            </a>
            {!jsonChanged && (
              <a onClick={handleSubmit} className="bordered-button blue">
                <i className="icon-check"></i> SUBMIT
              </a>
            )}
          </div>
        </FormBox>
      </div>

      <div className={`orders-form-edit-middle ${previewForm && showPreview ? '' : 'hidden'}`}>
        <RequestEdit formConfigurating={true} configurationForm={previewForm as OrdersFormViewModel} />
      </div>

      <div className="orders-form-edit-right">
        <FormBox title="Approval workflows" icon="icon-check">
          <div className="orders-form-edit-workflows">
            {sortedWorkflowIds.map((id) => (
              <>
                <div className="workflow-info">
                  <div className="workflow-info-title">
                    <i className={getWorkflowStateIcon(id)} style={{ color: getWorkflowStateColor(id) }}></i>
                    <NavLink to={`/RequestsManagement/EditWorkflow/${id}`}>
                      <div>{workflowNames[id] ?? '(Unknown)'}</div>
                      <div>{id}</div>
                    </NavLink>
                  </div>
                  {!!workflowTriggers[id]?.length && (
                    <i
                      className={expandedWorkflowInfo[id] ? 'icon-up' : 'icon-bottom'}
                      onClick={() => toggleWorkflowInfo(id)}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </div>
                {expandedWorkflowInfo[id] && (
                  <div className="workflow-triggers">
                    {workflowTriggers[id]?.map((t) => (
                      <span style={t.includes('(Unknown)') ? { color: 'red' } : undefined}>{t}</span>
                    ))}
                  </div>
                )}
              </>
            ))}
          </div>
        </FormBox>
      </div>
      <Alert
        onClose={() => setSavingError(undefined)}
        visible={!!savingError}
        title={t('ERROR')}
        message={savingError ?? ''}
      />
    </Loader>
  )
  // #endregion
}
